import React, { useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import rehypeSanitize from "rehype-sanitize";
import "highlight.js/styles/github-dark.css";
import { Components } from "react-markdown";
import { getSessionValidity, sendThumbsFeedback } from "../../../Rest_api/RestApi";
import { redirectToLogin } from "../../Protected";
import AgentChart, { DataPoint, PlotParameters } from "../../../components/agent/AgentChart";
import { AgentQuestions } from "./AgentQuestions";
import { SquarePen, Mic, MicOff, ThumbsUp, ThumbsDown, Check } from "lucide-react";
import {
  LogoIcon,
  LoadingSpinner,
  SendIcon,
  ChevronDownIcon,
  DownloadIcon,
} from "../../../components/agent/AgentIcons";
import { markdownStyles} from "../../../components/agent/AgentStyles";
import { ReactMic } from "react-mic";
import { AgentEvent } from "../../../models/agent";
import { useSearchParams, useNavigate } from "react-router-dom";
import { AGENT_API_BASE, BASE_URL } from "../../../configs";
import AgentTable from "../../../components/agent/AgentTable";

/**
 * Chat page that matches the supplied mock‑up and streams responses from the backend.
 * Technologies: React + TypeScript + TailwindCSS.
 *
 * – Creates a session on first mount and keeps its id in a ref.
 * – `sendMessage` POSTs to `/run_sse` and decodes the Server‑Sent‑Events stream.
 * – Replies from authors in MAIN_AUTHORS appear inline; other authors collapse under a <details> "Thinking …" control.
 */

interface ApiResponse {
  status: boolean;
  message?: string;
  data?: any;
}

type Author = "user" | string;

interface Message {
  id: string;
  author: Author;
  content: string;
  chartData?: DataPoint[];
  tableData?: any[];
  chartParameters?: PlotParameters;
  invocation_id?: string;
  functionCall?: boolean;
}

interface QuestionType {
  heading?: string;
  text: string;
  highlights?: string[];
}

interface ChartData {
  date: string;
  mwh: string;
  legend: string;
}


// Update these lists to tweak visibility rules without touching render code.
const MAIN_AUTHORS: Author[] = ["DataShowAgent", "gnai_ruvitl"];
const THINKING_AUTHORS: Author[] = [
  "PlotRequirementCreator",
  "PlotMetadata",
  "data_fetching_agent",
  "data_requirement_creator",
  "data_validation_agent",
  "plot_data_extractor",
  "SchemaExtractor",
  "PlotDataExtractor",
  "Checker",
];

const PRESET_QUESTIONS: string[] = [];
const API_BASE = AGENT_API_BASE;
// const API_BASE = "http://127.0.0.1:8001";
const APP_NAME = "ruvitl_beta";
const usertoken =   localStorage.getItem("token");

// Add these interfaces after the existing interfaces
interface RecordingState {
  isRecording: boolean;
  recognition: SpeechRecognition | null;
  duration: number;
  isLongPress: boolean;
}

// Add Web Speech API type definitions
interface SpeechRecognitionEvent extends Event {
  results: SpeechRecognitionResultList;
  resultIndex: number;
  interpretation: any;
}

interface SpeechRecognitionResultList {
  length: number;
  item(index: number): SpeechRecognitionResult;
  [index: number]: SpeechRecognitionResult;
}

interface SpeechRecognitionResult {
  isFinal: boolean;
  length: number;
  item(index: number): SpeechRecognitionAlternative;
  [index: number]: SpeechRecognitionAlternative;
}

interface SpeechRecognitionAlternative {
  transcript: string;
  confidence: number;
}

interface SpeechRecognitionError extends Event {
  error: string;
  message: string;
}

interface SpeechRecognition extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  maxAlternatives: number;
  onaudioend: ((this: SpeechRecognition, ev: Event) => any) | null;
  onaudiostart: ((this: SpeechRecognition, ev: Event) => any) | null;
  onend: ((this: SpeechRecognition, ev: Event) => any) | null;
  onerror:
    | ((this: SpeechRecognition, ev: SpeechRecognitionError) => any)
    | null;
  onnomatch: ((this: SpeechRecognition, ev: Event) => any) | null;
  onresult:
    | ((this: SpeechRecognition, ev: SpeechRecognitionEvent) => any)
    | null;
  onsoundend: ((this: SpeechRecognition, ev: Event) => any) | null;
  onsoundstart: ((this: SpeechRecognition, ev: Event) => any) | null;
  onspeechend: ((this: SpeechRecognition, ev: Event) => any) | null;
  onspeechstart: ((this: SpeechRecognition, ev: Event) => any) | null;
  onstart: ((this: SpeechRecognition, ev: Event) => any) | null;
  start(): void;
  stop(): void;
  abort(): void;
}

interface SpeechRecognitionConstructor {
  new (): SpeechRecognition;
  prototype: SpeechRecognition;
}

declare global {
  interface Window {
    SpeechRecognition: SpeechRecognitionConstructor;
    webkitSpeechRecognition: SpeechRecognitionConstructor;
  }
}

const ChartPlaceholder = ({
  data,
  parameters,
}: {
  data: DataPoint[];
  parameters: PlotParameters | null;
}) => {
  return <AgentChart data={data} chartPar={parameters} /> ;
};

// Add this placeholder PlusIcon at the top of the file or use an existing one
const PlusIcon = () => (
  <svg
    width="24"
    height="24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    viewBox="0 0 24 24"
  >
    <circle
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="2"
      fill="none"
    />
    <line
      x1="12"
      y1="8"
      x2="12"
      y2="16"
      stroke="currentColor"
      strokeWidth="2"
    />
    <line
      x1="8"
      y1="12"
      x2="16"
      y2="12"
      stroke="currentColor"
      strokeWidth="2"
    />
  </svg>
);

const SquareStopIcon = ({ className = "" }) => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    className={className}
  >
    <rect x="4" y="4" width="12" height="12" rx="3" fill="currentColor" />
  </svg>
);

// Helper to extract message text from content
function getMessageText(content: any): string {
  let text = "";
  
  if (typeof content === "string") {
    text = content;
  } else if (content && typeof content === "object" && content.text) {
    text = content.text;
  } else if (content && typeof content === "object" && content.parts && Array.isArray(content.parts)) {
    // Handle function call events
    const functionCall = content.parts.find((part: any) => part.functionCall);
    if (functionCall && functionCall.functionCall) {
      const funcName = functionCall.functionCall.name;
      const args = functionCall.functionCall.args;
      
      if (funcName === "code_executor_plot" && args && args.query) {
        text = `Executing query: ${args.query.substring(0, 100)}${args.query.length > 100 ? '...' : ''}`;
      }
      else if(funcName === "transfer_to_agent" && args && args.agent_name){
        text = "Transferring to "+args.agent_name;
      }
      else {
        text = `Executing ${funcName}`;
      }
    } else {
      // Handle regular parts
      text = content.parts.map((p: any) => p.text || JSON.stringify(p)).join(" ");
    }
  } else {
    text = JSON.stringify(content);
  }
  
  // Replace specific terms in the message text
  text = text.replace(/RUVITL/g, "Demo State");
  text = text.replace(/Rajasthan/g, "Demo State");
  text = text.replace(/AVVNL/g, "Discom 1");
  text = text.replace(/JVVNL/g, "Discom 2");
  text = text.replace(/JDVVNL/g, "Discom 3");
  
  return text;
}

// Helper function to convert agent names to user-friendly names
function getAgentDisplayName(agentName: string, content?: any): string {
  // Special handling for PlotDataExtractor with function calls
  if (agentName === "PlotDataExtractor" && content && typeof content === "object") {
    if (content.parts && Array.isArray(content.parts)) {
      const hasFunctionCall = content.parts.some((part: any) => part.functionCall);
      if (hasFunctionCall) {
        return "Executing Query";
      }
    }
  }
  
  switch (agentName) {
    case "PlotRequirementCreator":
      return "Creating Plot Requirements";
    case "PlotMetadata":
      return "Writing Chart Titles and Labels";
    case "data_fetching_agent":
      return "Fetching Data";
    case "data_requirement_creator":
      return "Creating Data Requirements";
    case "data_validation_agent":
      return "Validating Data";
    case "plot_data_extractor":
      return "Extracting Plot Data";
    case "data_processing_agent":
      return "Processing Data";
    case "data_analysis_agent":
      return "Analyzing Data";
    case "data_visualization_agent":
      return "Creating Visualization";
    case "data_insights_agent":
      return "Generating Insights";
    case "data_reporting_agent":
      return "Preparing Report";
    case "data_qa_agent":
      return "Quality Assurance";
    case "SchemaExtractor":
      return "Extracting Schema";
    case "PlotDataExtractor":
      return "Extracting Plot Data";
    case "Checker":
      return "Checking Data";
    default:
      return "Thinking";
  }
}

interface Session {
  id: string;
}

interface EventResponse {
  events: AgentEvent[];
}

// Extract InputBox as a separate stateless component
interface InputBoxProps {
  input: string;
  setInput: (value: string) => void;
  sendMessage: (text: string) => void;
  isLoading: boolean;
  isSessionLoading: boolean;
  isSessionCreating: boolean;
  isTranscribing: boolean;
  recordingState: RecordingState;
  handleMicPress: () => void;
  handleMicRelease: () => void;
}

const InputBox: React.FC<InputBoxProps> = ({
  input,
  setInput,
  sendMessage,
  isLoading,
  isSessionLoading,
  isSessionCreating,
  isTranscribing,
  recordingState,
  handleMicPress,
  handleMicRelease,
}) => {
  return (
    <div className="p-3 rounded-[28px] bg-clip-padding contain-inline-size overflow-clip border-token-border-default border shadow-sm sm:shadow-lg bg-token-bg-primary">
      <input
        className="w-full bg-transparent outline-none text-lg px-2"
        placeholder="Message Vidyut AI..."
        value={input}
        style={{ fontFamily: "inherit" }}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            if (input.trim()) {
              sendMessage(input);
            }
          }
        }}
        disabled={isSessionLoading || isLoading || isSessionCreating}
      />
      <div className="flex justify-end gap-2 mt-1">
        <button
          className="rounded-full bg-gray-100 p-3 flex items-center justify-center"
          onMouseDown={handleMicPress}
          onMouseUp={handleMicRelease}
          onMouseLeave={handleMicRelease}
          onTouchStart={handleMicPress}
          onTouchEnd={handleMicRelease}
          title="Tap to start/stop recording, hold for quick record"
        >
          {isTranscribing ? (
            <LoadingSpinner />
          ) : recordingState.isRecording ? (
            <SquareStopIcon className="w-5 h-5 text-gray-600" />
          ) : (
            <Mic className="w-5 h-5 text-gray-600" />
          )}
        </button>
        <button
          className="rounded-full bg-[#f1935c] hover:bg-[#e67f45] text-white p-3 flex items-center justify-center transition-colors"
          onClick={() => {
            if (input.trim()) {
              sendMessage(input);
            }
          }}
          disabled={isLoading}
          style={{ fontFamily: "inherit" }}
        >
          {isLoading ? <LoadingSpinner /> : <SendIcon />}
        </button>
      </div>
    </div>
  );
};

export default function RajAgent() {
  /** Chat state */
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [hasMessages, setHasMessages] = useState(false);
  const [selectedTab, setSelectedTab] = useState<string>("Recommendations");
  const [isLoading, setIsLoading] = useState(false);
  const [isSessionCreating, setIsSessionCreating] = useState(false);
  const [sessionError, setSessionError] = useState<string | null>(null);
  const [currentPresetIndex, setCurrentPresetIndex] = useState(0);
  const [ratedMessages, setRatedMessages] = useState<Record<string, { isUp: boolean; isLoading: boolean }>>({});
  const sessionIdRef = useRef<string | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const pendingPlotParamsRef = useRef<PlotParameters | null>(null);
  const [recordingState, setRecordingState] = useState<RecordingState>({
    isRecording: false,
    recognition: null,
    duration: 0,
    isLongPress: false,
  });
  const [speechError, setSpeechError] = useState<string | null>(null);
  const durationIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const longPressTimerRef = useRef<NodeJS.Timeout | null>(null);
  const pressStartTimeRef = useRef<number>(0);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const retryCountRef = useRef(0);
  const MAX_RETRIES = 3;
  const [isRetrying, setIsRetrying] = useState(false);
  const [isBrowserSupported, setIsBrowserSupported] = useState<boolean | null>(
    null
  );
  // New state for sidebar
  const [selectedSession, setSelectedSession] = useState<Session | null>(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionIdFromUrl = searchParams.get("session");
  const [isSessionLoading, setIsSessionLoading] = useState(false);
  const [sessionLoadError, setSessionLoadError] = useState<string | null>(null);

  // Tab questions
  const TAB_QUESTIONS: Record<
    string,
    Array<{ heading: string; questions: QuestionType[] }>
  > = AgentQuestions;

  // Function to run preset questions in sequence
  const runPresetQuestions = async () => {
    if (currentPresetIndex < PRESET_QUESTIONS.length) {
      const question = PRESET_QUESTIONS[currentPresetIndex];
      await sendMessage(question);
      setCurrentPresetIndex((prev) => prev + 1);
    }
  };

  // Run preset questions when messages change and current message is complete
  useEffect(() => {
    if (
      messages.length > 0 &&
      !isLoading &&
      currentPresetIndex < PRESET_QUESTIONS.length
    ) {
      const lastMessage = messages[messages.length - 1];
      if (lastMessage.author !== "user") {
        // Wait a bit before sending next question
        const timer = setTimeout(runPresetQuestions, 1000);
        return () => clearTimeout(timer);
      }
    }
  }, [messages, isLoading]);

  /** Auto‑scroll on new messages */
  useEffect(() => {
    const scrollToBottom = () => {
      if (listRef.current) {
        // Force scroll to bottom immediately when new messages arrive
        listRef.current.scrollTop = listRef.current.scrollHeight;
      }
    };

    // Call immediately
    scrollToBottom();

    // Also call after a short delay to handle dynamic content
    const timeoutId = setTimeout(scrollToBottom, 100);

    return () => clearTimeout(timeoutId);
  }, [messages]);

  /** Check session validity and create session on mount */
  // useEffect(() => {
  //   // First check session validity
  //   getSessionValidity().then((res: ApiResponse) => {
  //     if (res.status === false) {
  //       redirectToLogin(window.location.href);
  //       return;
  //     }

  //     // If session is valid, create a new chat session
  //     console.log("Creating session...");
  //     fetch(`${API_BASE}/apps/${APP_NAME}/users/${usertoken}/sessions`, {
  //       method: "POST",
  //     })
  //       .then((res) => res.json())
  //       .then((data: any) => {
  //         console.log("Session created:", data);
  //         if (data["id"]) {
  //           sessionIdRef.current = data["id"];
  //           console.log("Session ID set:", sessionIdRef.current);
  //           // Start running preset questions after session is created
  //           runPresetQuestions();
  //         }
  //       })
  //       .catch((error) => {
  //         console.error("Error creating session:", error);
  //       });
  //   });
  // }, []);

  useEffect(() => {
    // Add the styles to the document head
    const styleElement = document.createElement("style");
    styleElement.innerHTML = markdownStyles;
    document.head.appendChild(styleElement);

    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  // Add this useEffect to check browser support on component mount
  useEffect(() => {
    const checkBrowserSupport = () => {
      const isSupported = !!(
        window.SpeechRecognition || window.webkitSpeechRecognition
      );
      setIsBrowserSupported(isSupported);
      if (!isSupported) {
        setSpeechError(
          "Speech recognition is not supported in your browser. Please use Chrome, Edge, or Safari."
        );
      }
    };
    checkBrowserSupport();
  }, []);

  // New Chat handler
  const handleNewChat = () => {
    setMessages([]);
    setInput("");
    setHasMessages(false);
    setSessionLoadError(null);
    sessionIdRef.current = null;
    setSelectedSession(null);
    pendingPlotParamsRef.current = null; // Clear pending plot parameters
    // Remove session param from URL
    const params = new URLSearchParams(window.location.search);
    params.delete("session");
    navigate(`?${params.toString()}`, { replace: true });
  };

  // Scroll to bottom helper
  const scrollToBottom = () => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  };

  // useEffect for sessionIdFromUrl

  // Scroll to bottom on new message
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  /** Send a user message and stream the response */
  const sendMessage = async (text: string) => {
    setSessionError(null); // Clear previous error
    if (!text.trim()) {
      return;
    }
    let isNewSession = false;
    // If no session, create one first
    if (!sessionIdRef.current) {
      isNewSession = true;
      setIsSessionCreating(true);
      try {
        const res = await getSessionValidity();
        if (res.status === false) {
          redirectToLogin(window.location.href);
          setIsSessionCreating(false);
          return;
        }
        // Create a new chat session
        const data = await fetch(`${API_BASE}/apps/${APP_NAME}/users/${usertoken}/sessions`, {
          method: "POST",
        }).then((res) => res.json());
        if (data && data["id"]) {
          sessionIdRef.current = data["id"];
          // Update the URL with the new session id without causing a refresh
          const params = new URLSearchParams(window.location.search);
          params.set("session", sessionIdRef.current || "");
          window.history.replaceState({}, '', `?${params.toString()}`);
        } else {
          setSessionError("Could not start chat, please try again later.");
          setIsSessionCreating(false);
          return;
        }
      } catch (error) {
        setSessionError("Could not start chat, please try again later.");
        setIsSessionCreating(false);
        return;
      }
      setIsSessionCreating(false);
    }
    setIsLoading(true);
    setHasMessages(true);
    // Clear pending plot parameters when starting a new conversation
    pendingPlotParamsRef.current = null;
    if (isNewSession) {
      setMessages([{ id: crypto.randomUUID(), author: "user", content: text }]);
    } else {
      setMessages((m) => [
        ...m,
        { id: crypto.randomUUID(), author: "user", content: text },
      ]);
    }
    setInput("");
    try {
      const resp = await fetch(`${API_BASE}/run_sse`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "text/event-stream",
        },
        body: JSON.stringify({
          app_name: APP_NAME,
          usertoken: usertoken,
          session_id: sessionIdRef.current, // Always use the active session id
          new_message: { role: "user", parts: [{ text }] },
          streaming: true,
        }),
      });
      if (!resp.body) {
        setIsLoading(false);
        return;
      }
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          setIsLoading(false);
          // Clear pending plot parameters when streaming is complete
          pendingPlotParamsRef.current = null;
          break;
        }
        buffer += decoder.decode(value, { stream: true });
        const packets = buffer.split("\n\n");
        buffer = packets.pop()!;
        for (const p of packets) {
          if (!p.startsWith("data:")) continue;
          const jsonStr = p.replace(/^data:\s*/, "").trim();
          let evt: any;
          try {
            evt = JSON.parse(jsonStr);
            // Debug log to see event structure
            console.log("Received SSE event:", evt);
          } catch (error) {
            continue;
          }
          let author: Author = evt.author || "model";
          const plotParams = evt.actions?.stateDelta?.plot_metadata;
          const txt = (evt.content?.parts || [])
            .map((pt: any) => pt.text || "")
            .join("");

          if (author === "chartData") {
            console.log("Received chart data:", evt.content); // Debug log
            console.log("Current pending plot params:", pendingPlotParamsRef.current); // Debug log
            setMessages((m) => {
              const newMessage: Message = {
                id: evt.invocation_id,
                author: "chart",
                content: "",
                chartData: Array.isArray(evt.content) ? evt.content : [evt.content],
                chartParameters: pendingPlotParamsRef.current || undefined,
              };
              return [...m, newMessage];
            });
            // Don't clear pendingPlotParamsRef here - let it persist for potential future chart data
            continue;
          }
          if(author === "tableData"){
            console.log("Received table data:", evt.content); // Debug log
            setMessages((m) => {
              const newMessage: Message = {
                id: evt.invocation_id,
                author: "table",
                content: "",
                tableData: evt.content,
              };
              return [...m, newMessage];
            });
            continue;
          }

          if (plotParams) {
            console.log("Received plot parameters:", plotParams); // Debug log
            const params = plotParams as PlotParameters;
            pendingPlotParamsRef.current = params;
            console.log("Updated pending plot params:", pendingPlotParamsRef.current); // Debug log
            
            // Update the most recent chart message with the new parameters
            setMessages((m) => {
              const chartMessages = m.filter(msg => msg.author === "chart");
              if (chartMessages.length > 0) {
                const lastChartMessage = chartMessages[chartMessages.length - 1];
                const lastChartIndex = m.findIndex(msg => msg.id === lastChartMessage.id);
                if (lastChartIndex !== -1) {
                  const updatedMessages = [...m];
                  updatedMessages[lastChartIndex] = {
                    ...lastChartMessage,
                    chartParameters: params
                  };
                  console.log("Updated chart message with parameters:", updatedMessages[lastChartIndex]); // Debug log
                  return updatedMessages;
                }
              }
              return m;
            });
            
            continue;
          }
  
          if (evt.partial) continue;
          
          // Check if this is a function call event
          const hasFunctionCall = evt.content?.parts?.some((part: any) => part.functionCall);
          
          // Skip if no text content and no function call
          if (!txt && !hasFunctionCall) continue;

          // Debug log for message creation
          console.log("Creating message with event:", {
            author,
            txt,
            hasFunctionCall,
            invocation_id: evt.invocation_id || evt.invocationId,
            thumbs_up: evt.thumbs_up
          });

          setMessages((m) => {
            const last = m[m.length - 1];
            const invocationId = evt.invocation_id || evt.invocationId;
            
            if (
              last &&
              last.author === author &&
              author !== "user" &&
              author !== "chart"
            ) {
              // Update existing message
              const updatedMessage = { 
                ...last, 
                content: last.content + (txt || ""), 
                functionCall: evt.content?.parts?.some((part: any) => part.functionCall),
                invocation_id: invocationId 
              };

              // Check for thumbs_up in the event
              if (invocationId && evt.thumbs_up !== undefined) {
                setRatedMessages(prev => ({
                  ...prev,
                  [invocationId]: {
                    isUp: evt.thumbs_up === true,
                    isLoading: false
                  }
                }));
              }

              return [...m.slice(0, -1), updatedMessage];
            }

            // Create new message
            const newMessage = {
              id: crypto.randomUUID(),
              author,
              content: txt || evt.content, // Use full content for function calls
              invocation_id: invocationId
            };

            // Check for thumbs_up in the event
            if (invocationId && evt.thumbs_up !== undefined) {
              setRatedMessages(prev => ({
                ...prev,
                [invocationId]: {
                  isUp: evt.thumbs_up === true,
                  isLoading: false
                }
              }));
            }

            return [...m, newMessage];
          });
        }
      }
    } catch (error) {
      // Optionally set a message error here
      setIsLoading(false);
      // Clear pending plot parameters on error
      pendingPlotParamsRef.current = null;
    }
  };

  /** Helper for suggestion buttons */
  const quickAsk = (prompt: string) => sendMessage(prompt);

  const convertTableToCSV = (table: HTMLTableElement): string => {
    const rows = Array.from(table.querySelectorAll("tr"));
    return rows
      .map((row) => {
        const cells = Array.from(row.querySelectorAll("th, td"));
        return cells
          .map((cell) => {
            const text = cell.textContent?.trim() || "";
            // Escape quotes and wrap in quotes if contains comma
            return text.includes(",") ? `"${text.replace(/"/g, '""')}"` : text;
          })
          .join(",");
      })
      .join("\n");
  };

  const downloadTableAsCSV = (table: HTMLTableElement) => {
    const csv = convertTableToCSV(table);
    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "table_data.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  // Refresh session handler
  const refreshSession = () => {
    setMessages([]);
    setHasMessages(false);
    setCurrentPresetIndex(0);
    sessionIdRef.current = null;
    pendingPlotParamsRef.current = null; // Clear pending plot parameters
    getSessionValidity().then((res: ApiResponse) => {
      if (res.status === false) {
        redirectToLogin(window.location.pathname);
        return;
      }
      fetch(`${API_BASE}/apps/${APP_NAME}/users/${usertoken}/sessions`, {
        method: "POST",
      })
        .then((res) => res.json())
        .then((data: any) => {
          if (data["id"]) {
            sessionIdRef.current = data["id"];
            runPresetQuestions();
          }
        })
        .catch((error) => {
          console.error("Error creating session:", error);
        });
    });
  };

  // Add this function after other functions
  const startRecording = async (isLongPress: boolean = false) => {
    try {
      // Reset retry count if starting fresh
      if (!recordingState.isRecording) {
        retryCountRef.current = 0;
      }

      // Check if browser supports speech recognition
      if (!isBrowserSupported) {
        setSpeechError(
          "Speech recognition is not supported in your browser. Please use Chrome, Edge, or Safari."
        );
        return;
      }

      setSpeechError(null); // Clear any previous errors
      const SpeechRecognition =
        window.SpeechRecognition || window.webkitSpeechRecognition;
      const recognition = new SpeechRecognition();

      recognition.continuous = false;
      recognition.interimResults = false;
      recognition.lang = "en-US";

      recognition.onresult = (event: SpeechRecognitionEvent) => {
        const transcript = event.results[0][0].transcript;
        setInput(transcript);
        if (recordingState.isLongPress) {
          sendMessage(transcript);
        }
        // Reset retry count on successful recognition
        retryCountRef.current = 0;
        setIsRetrying(false);
      };

      recognition.onend = () => {
        if (durationIntervalRef.current) {
          clearInterval(durationIntervalRef.current);
        }
        setRecordingState((prev) => ({
          ...prev,
          isRecording: false,
          recognition: null,
          duration: 0,
          isLongPress: false,
        }));
        setIsRetrying(false);
      };

      recognition.onerror = (event: SpeechRecognitionError) => {
        console.error("Speech recognition error:", event.error);

        if (event.error === "network" && retryCountRef.current < MAX_RETRIES) {
          retryCountRef.current += 1;
          console.log(
            `Retrying speech recognition (attempt ${retryCountRef.current}/${MAX_RETRIES})`
          );
          setIsRetrying(true);
          // Wait a bit before retrying
          setTimeout(() => {
            if (recordingState.isRecording) {
              recognition.stop();
              startRecording(isLongPress);
            }
          }, 1000);
          return;
        }

        let errorMessage = "Speech recognition error occurred";

        switch (event.error) {
          case "network":
            errorMessage =
              "Network error. Please check your internet connection and try again.";
            break;
          case "not-allowed":
            errorMessage =
              "Microphone access was denied. Please allow microphone access.";
            break;
          case "aborted":
            errorMessage = "Speech recognition was aborted.";
            break;
          case "audio-capture":
            errorMessage =
              "No microphone was found. Please ensure a microphone is installed.";
            break;
          case "no-speech":
            errorMessage = "No speech was detected. Please try again.";
            break;
          case "service-not-allowed":
            errorMessage = "Speech recognition service is not allowed.";
            break;
        }

        setSpeechError(errorMessage);
        setRecordingState((prev) => ({
          ...prev,
          isRecording: false,
          recognition: null,
          duration: 0,
          isLongPress: false,
        }));
        // Reset retry count after max retries
        retryCountRef.current = 0;
        setIsRetrying(false);
      };

      recognition.start();
      setRecordingState((prev) => ({
        ...prev,
        isRecording: true,
        recognition,
        duration: 0,
        isLongPress,
      }));

      // Start duration timer
      durationIntervalRef.current = setInterval(() => {
        setRecordingState((prev) => ({
          ...prev,
          duration: prev.duration + 1,
        }));
      }, 1000);
    } catch (error) {
      console.error("Error starting speech recognition:", error);
      setSpeechError("Failed to start speech recognition. Please try again.");
      retryCountRef.current = 0;
      setIsRetrying(false);
    }
  };

  const stopRecording = () => {
    if (recordingState.recognition && recordingState.isRecording) {
      recordingState.recognition.stop();
    }
  };

  const handleMicPress = () => {
    pressStartTimeRef.current = Date.now();
    longPressTimerRef.current = setTimeout(() => {
      // If we reach here, it's a long press
      startRecording(true);
    }, 200); // 200ms threshold for long press
  };

  const handleMicRelease = () => {
    const pressDuration = Date.now() - pressStartTimeRef.current;

    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
    }

    if (pressDuration < 200) {
      // It's a tap
      if (recordingState.isRecording) {
        stopRecording();
      } else {
        startRecording(false);
      }
    } else if (recordingState.isRecording && recordingState.isLongPress) {
      // It's a long press release
      stopRecording();
    }
  };

  // Format duration to MM:SS
  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs
      .toString()
      .padStart(2, "0")}`;
  };

  const handleFeedback = async (invocationId: string, thumbsUp: boolean) => {
    try {
      // Set loading state for this message
      setRatedMessages(prev => ({
        ...prev,
        [invocationId]: { isUp: thumbsUp, isLoading: true }
      }));

      const result = await sendThumbsFeedback(invocationId, thumbsUp);
      if ('error' in result) {
        console.error("Failed to send feedback:", result.error);
        // Remove the rating on error
        setRatedMessages(prev => {
          const newState = { ...prev };
          delete newState[invocationId];
          return newState;
        });
      } else {
        // Keep the selected state but remove loading
        setRatedMessages(prev => ({
          ...prev,
          [invocationId]: { isUp: thumbsUp, isLoading: false }
        }));
      }
    } catch (error) {
      console.error("Error sending feedback:", error);
      // Remove the rating on error
      setRatedMessages(prev => {
        const newState = { ...prev };
        delete newState[invocationId];
        return newState;
      });
    }
  };

  // Handler to select session and update URL (do not set selectedSession directly)
  const handleSelectSession = (session: Session) => {
    const params = new URLSearchParams(window.location.search);
    params.set("session", session.id || "");
    navigate(`?${params.toString()}`, { replace: true });
  };

  return (
    <div className="w-full bg-[#f6faf9] flex flex-row overflow-hidden" style={{ height: "100%" }}>
      {/* Sidebar for chat history */}
      {/* <AgentSessionSidebar
        onSelectSession={handleSelectSession}
        onNewChat={handleNewChat}
        selectedSessionId={selectedSession ? selectedSession.id : null}
        app_name={APP_NAME}
      /> */}
      {/* Main chat area */}
      <div style={{ flex: 1, minWidth: 0 }}>
        {isSessionLoading ? (
          <div className="flex items-center justify-center h-full">
            <LoadingSpinner />
          </div>
        ) : (
          <div className="w-full bg-white flex flex-col overflow-hidden" style={{ height: "100%" }}>

            <div className="flex-1 flex flex-col items-center justify-center h-full w-full overflow-hidden">
              <div className="w-full h-full mx-auto bg-white  shadow-xl  flex flex-col items-center overflow-y-hidden">
                {/* hero and quick actions - only show when no messages */}
                {!hasMessages && (
                  <main className="flex-1 flex flex-col items-center md:px-4 px-2 md:pt-8 pt-4 w-full justify-center">
                    <div className="w-full text-center max-w-2xl">
                      <h2
                        className="text-2xl md:text-4xl font-semibold text-gray-800 mb-2"
                        style={{ fontFamily: "inherit", letterSpacing: "-0.02em" }}
                      >
                        Hello, User!
                      </h2>
                      <p
                        className="text-lg md:text-2xl text-gray-500 font-normal mb-4"
                        style={{ fontFamily: "inherit" }}
                      >
                        How Can I Help You Today?
                      </p>
                      {/* Input box - now a textarea, taller, and more margin below */}
                      <div className=" bg-white w-full bottom-0 left-0 z-20">
                        <div className="mb-4 px-3 max-w-2xl mx-auto">
                          {sessionError && (
                            <div className="text-red-600 text-center mb-2 font-medium bg-red-50 border border-red-200 rounded-lg p-2">
                              {sessionError}
                            </div>
                          )}
                          <InputBox
                            input={input}
                            setInput={setInput}
                            sendMessage={sendMessage}
                            isLoading={isLoading}
                            isSessionLoading={isSessionLoading}
                            isSessionCreating={isSessionCreating}
                            isTranscribing={isTranscribing}
                            recordingState={recordingState}
                            handleMicPress={handleMicPress}
                            handleMicRelease={handleMicRelease}
                          />       
                        </div>
                      </div>
                    </div>
                    {/* Tabs and recommendations */}
                    <div className="w-full max-w-6xl mx-auto flex flex-col h-[calc(100vh-400px)]">
                      {/* Tabs */}
                      <div className="flex flex-wrap w-full justify-center gap-2 mb-4 md:overflow-x-hidden">
                        {Object.keys(TAB_QUESTIONS).map((tab) => (
                          <button
                            key={tab}
                            className={`md:px-6 px-3 py-1.5 text-sm md:text-base font-medium rounded-full border transition-colors duration-200 whitespace-nowrap ${
                              selectedTab === tab
                                ? "bg-[#f1935c] text-white border-[#f1935c]"
                                : "bg-white text-gray-600 border-gray-300 hover:bg-[#f1935c]/10 hover:text-[#f1935c]"
                            }`}
                            style={{ fontFamily: "inherit" }}
                            onClick={() => setSelectedTab(tab)}
                          >
                            {tab}
                          </button>
                        ))}
                      </div>
                      {/* Responsive suggestion cards in grid */}
                      <div className="flex-1 overflow-y-auto">
                        <div className="flex flex-wrap gap-3 justify-center pb-4">
                          {TAB_QUESTIONS[selectedTab].map((section) =>
                            section.questions.map((q, idx) => (
                              <button
                                key={`${selectedTab}-${idx}`}
                                onClick={() => setInput(q.text)}
                                className="bg-white w-full md:w-auto border border-gray-200 rounded-xl py-2 px-3 hover:bg-[#f1935c]/10 transition-all duration-200 text-gray-800 text-left shadow-sm font-normal text-[14px] min-h-[60px] max-w-[300px] flex items-center"
                                style={{ fontFamily: "inherit" }}
                              >
                                <span className="whitespace-pre-wrap leading-relaxed">
                                  {q.highlights
                                    ? q.text
                                        .split(
                                          new RegExp(
                                            `(${q.highlights
                                              .map((h) =>
                                                h.replace(
                                                  /[.*+?^${}()|[\]\\]/g,
                                                  "\\$&"
                                                )
                                              )
                                              .join("|")})`,
                                            "g"
                                          )
                                        )
                                        .map((part: string, i: number) =>
                                          q.highlights?.includes(part) ? (
                                            <span
                                              key={i}
                                              className="text-[#f1935c] font-medium"
                                            >
                                              {part}
                                            </span>
                                          ) : (
                                            <span key={i}>{part}</span>
                                          )
                                        )
                                    : q.text}
                                </span>
                              </button>
                            ))
                          )}
                        </div>
                      </div>
                    </div>
                  </main>
                )}
                {/* Chat layout after first message - revert to previous style */}
                {hasMessages && (
                  <main className="flex-1 flex flex-col h-full items-center md:px-4 w-full">
                    {/* message list in scrollable area inside white container */}
                    <div
                      className="flex-1 w-full overflow-y-scroll h-full px-2 md:px-8 pt-3 md:pt-6"
                      style={{ scrollBehavior: "smooth" }}
                      ref={listRef}
                    >
                      <div className="max-w-6xl mx-auto w-full space-y-6 pb-4">
                        {(() => {
                          // Process messages in chronological order and group thinking with the most recent user message
                          const allMessages: JSX.Element[] = [];
                          let currentThinkingMessages: Message[] = [];
                          let lastUserMessageId: string | null = null;

                          // Process messages in order
                          messages.forEach((msg) => {
                            // If this is a user message, add it and any pending thinking messages
                            if (msg.author === "user") {
                              // Add any pending thinking messages from previous conversation
                              if (currentThinkingMessages.length > 0) {
                                const latestThinkingAgent = currentThinkingMessages[currentThinkingMessages.length - 1].author;
                                
                                allMessages.push(
                                  <div key={`thinking-group-${lastUserMessageId || 'previous'}`} className="w-full first:mt-2">
                                    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                                      <details className="group">
                                        <summary className="flex items-center gap-3 px-4 py-3 cursor-pointer bg-gray-50 border-b border-gray-100">
                                          <div className="flex items-center gap-2 text-[#34656d]">
                                            {isLoading ? <LoadingSpinner /> : (
                                              <div className="w-4 h-4 rounded-full bg-green-500 flex items-center justify-center">
                                                <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                </svg>
                                              </div>
                                            )}
                                            <span className="font-medium">
                                              {getAgentDisplayName(latestThinkingAgent, currentThinkingMessages[currentThinkingMessages.length - 1]?.content)}
                                            </span>
                                          </div>
                                          <span className="text-sm text-gray-500">
                                            ({currentThinkingMessages.length} steps)
                                          </span>
                                          <ChevronDownIcon />
                                        </summary>
                                        <div className="px-4 py-3 space-y-4">
                                          {currentThinkingMessages.map((thinkingMsg, idx) => (
                                            <div key={thinkingMsg.id} className="border-l-2 border-gray-200 pl-4">
                                              <div className="flex items-center gap-2 mb-2">
                                                <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-1 rounded">
                                                  Step {idx + 1}
                                                </span>
                                                <span className="text-xs text-gray-400">
                                                  {getAgentDisplayName(thinkingMsg.author, thinkingMsg.content)}
                                                </span>
                                                <div className="ml-auto">
                                                  <div className="w-4 h-4 rounded-full bg-green-500 flex items-center justify-center">
                                                    <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                    </svg>
                                                  </div>
                                                </div>
                                              </div>
                                              <div className="text-sm text-gray-600">
                                                {getMessageText(thinkingMsg.content)}
                                              </div>
                                            </div>
                                          ))}
                                        </div>
                                      </details>
                                    </div>
                                  </div>
                                );
                                currentThinkingMessages = []; // Clear after adding
                              }

                              // Add the user message
                              allMessages.push(
                                <div key={msg.id} className="w-full first:mt-2">
                                  <div className="flex justify-end">
                                    <div className="inline-block px-4 md:px-6 py-3 md:py-4 rounded-2xl shadow-sm max-w-[90%] md:max-w-[85%] bg-gradient-to-r from-[#f1935c] to-[#e67f45] text-white">
                                      <div className="markdown-body" style={{ fontFamily: "inherit" }}>
                                        {getMessageText(msg.content)}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              );
                              lastUserMessageId = msg.id;
                            }

                            // Collect thinking messages
                            else if (THINKING_AUTHORS.includes(msg.author) || !(msg.author!=="PlotDataExtractor" && !msg.functionCall)) {
                              currentThinkingMessages.push(msg);
                            }
                            // Handle output messages (non-thinking, non-user, non-chart)
                            else if (msg.author !== "chart" && msg.author !== "table") {
                              // Add any pending thinking messages before the output
                              if (currentThinkingMessages.length > 0) {
                                const latestThinkingAgent = currentThinkingMessages[currentThinkingMessages.length - 1].author;
                                
                                allMessages.push(
                                  <div key={`thinking-group-${lastUserMessageId || 'current'}`} className="w-full first:mt-2">
                                    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                                      <details className="group">
                                        <summary className="flex items-center gap-3 px-4 py-3 cursor-pointer bg-gray-50 border-b border-gray-100">
                                          <div className="flex items-center gap-2 text-[#34656d]">
                                            {isLoading ? <LoadingSpinner /> : (
                                              <div className="w-4 h-4 rounded-full bg-green-500 flex items-center justify-center">
                                                <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                </svg>
                                              </div>
                                            )}
                                            <span className="font-medium">
                                              {getAgentDisplayName(latestThinkingAgent, currentThinkingMessages[currentThinkingMessages.length - 1]?.content)}
                                            </span>
                                          </div>
                                          <span className="text-sm text-gray-500">
                                            ({currentThinkingMessages.length} steps)
                                          </span>
                                          <ChevronDownIcon />
                                        </summary>
                                        <div className="px-4 py-3 space-y-4">
                                          {currentThinkingMessages.map((thinkingMsg, idx) => (
                                            <div key={thinkingMsg.id} className="border-l-2 border-gray-200 pl-4">
                                              <div className="flex items-center gap-2 mb-2">
                                                <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-1 rounded">
                                                  Step {idx + 1}
                                                </span>
                                                <span className="text-xs text-gray-400">
                                                  {getAgentDisplayName(thinkingMsg.author, thinkingMsg.content)}
                                                </span>
                                                <div className="ml-auto">
                                                  <div className="w-4 h-4 rounded-full bg-green-500 flex items-center justify-center">
                                                    <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                    </svg>
                                                  </div>
                                                </div>
                                              </div>
                                              <div className="text-sm text-gray-600">
                                                {getMessageText(thinkingMsg.content)}
                                              </div>
                                            </div>
                                          ))}
                                        </div>
                                      </details>
                                    </div>
                                  </div>
                                );
                                currentThinkingMessages = []; // Clear after adding
                              }

                              // Add the output message
                              if(getMessageText(msg.content).includes("Transferring to") || getMessageText(msg.content).slice(0,9)==="Executing"){
                              }else{  
                              allMessages.push(
                                <div key={msg.id} className="w-full first:mt-2">
                                  <div className="flex justify-start">
                                    <div className="inline-block px-4 md:px-6 py-3 md:py-4 rounded-2xl shadow-sm max-w-[90%] md:max-w-[85%] bg-[#34656d] text-white">
                                      <div className="relative">
                                        <div className="markdown-body prose prose-sm max-w-none prose-invert" style={{ fontFamily: "inherit" }}>
                                          {TableMarkdown(msg)}
                                        </div>
                                        {msg.invocation_id && (
                                          <div className="flex gap-2 mt-2 justify-end h-8">
                                            {ratedMessages[msg.invocation_id]?.isLoading ? (
                                              <div className="flex items-center justify-center w-8 h-8">
                                                <LoadingSpinner className="w-4 h-4" />
                                              </div>
                                            ) : (
                                              <>
                                                <button
                                                  onClick={() => handleFeedback(msg.invocation_id!, true)}
                                                  className={`w-8 h-8 flex items-center justify-center rounded-full transition-colors ${
                                                    ratedMessages[msg.invocation_id]?.isUp 
                                                      ? 'bg-white/20' 
                                                      : 'hover:bg-white/10'
                                                  }`}
                                                  title="Thumbs up"
                                                >
                                                  <ThumbsUp className={`w-4 h-4 ${
                                                    ratedMessages[msg.invocation_id]?.isUp 
                                                      ? 'text-green-400' 
                                                      : ''
                                                  }`} />
                                                </button>
                                                <button
                                                  onClick={() => handleFeedback(msg.invocation_id!, false)}
                                                  className={`w-8 h-8 flex items-center justify-center rounded-full transition-colors ${
                                                    ratedMessages[msg.invocation_id]?.isUp === false 
                                                      ? 'bg-white/20' 
                                                      : 'hover:bg-white/10'
                                                  }`}
                                                  title="Thumbs down"
                                                >
                                                  <ThumbsDown className={`w-4 h-4 ${
                                                    ratedMessages[msg.invocation_id]?.isUp === false 
                                                      ? 'text-red-400' 
                                                      : ''
                                                  }`} />
                                                </button>
                                              </>
                                            )}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              );}
                            }
                            // Handle chart messages
                            else if (msg.author === "chart") {
                              // Add any pending thinking messages before the chart
                              if (currentThinkingMessages.length > 0) {
                                const latestThinkingAgent = currentThinkingMessages[currentThinkingMessages.length - 1].author;
                                
                                allMessages.push(
                                  <div key={`thinking-group-${lastUserMessageId || 'before-chart'}`} className="w-full first:mt-2">
                                    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                                      <details className="group">
                                        <summary className="flex items-center gap-3 px-4 py-3 cursor-pointer bg-gray-50 border-b border-gray-100">
                                          <div className="flex items-center gap-2 text-[#34656d]">
                                            {isLoading ? <LoadingSpinner /> : (
                                              <div className="w-4 h-4 rounded-full bg-green-500 flex items-center justify-center">
                                                <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                </svg>
                                              </div>
                                            )}
                                            <span className="font-medium">
                                              {getAgentDisplayName(latestThinkingAgent, currentThinkingMessages[currentThinkingMessages.length - 1]?.content)}
                                            </span>
                                          </div>
                                          <span className="text-sm text-gray-500">
                                            ({currentThinkingMessages.length} steps)
                                          </span>
                                          <ChevronDownIcon />
                                        </summary>
                                        <div className="px-4 py-3 space-y-4">
                                          {currentThinkingMessages.map((thinkingMsg, idx) => (
                                            <div key={thinkingMsg.id} className="border-l-2 border-gray-200 pl-4">
                                              <div className="flex items-center gap-2 mb-2">
                                                <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-1 rounded">
                                                  Step {idx + 1}
                                                </span>
                                                <span className="text-xs text-gray-400">
                                                  {getAgentDisplayName(thinkingMsg.author, thinkingMsg.content)}
                                                </span>
                                                <div className="ml-auto">
                                                  <div className="w-4 h-4 rounded-full bg-green-500 flex items-center justify-center">
                                                    <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                    </svg>
                                                  </div>
                                                </div>
                                              </div>
                                              <div className="text-sm text-gray-600">
                                                {getMessageText(thinkingMsg.content)}
                                              </div>
                                            </div>
                                          ))}
                                        </div>
                                      </details>
                                    </div>
                                  </div>
                                );
                                currentThinkingMessages = []; // Clear after adding
                              }
                              allMessages.push(
                                <ChartPlaceholder
                                  key={msg.id}
                                  data={msg.chartData!}
                                  parameters={msg.chartParameters || null}
                                />
                              );
                            }
                            else if (msg.author === "table") {
                              allMessages.push(
                                <AgentTable tableData={msg.tableData!} />
                               )}

                          });

                          // Add any remaining thinking messages at the end
                          if (currentThinkingMessages.length > 0) {
                            const latestThinkingAgent = currentThinkingMessages[currentThinkingMessages.length - 1].author;
                            
                            allMessages.push(
                              <div key={`thinking-group-final`} className="w-full first:mt-2">
                                <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                                  <details className="group">
                                    <summary className="flex items-center gap-3 px-4 py-3 cursor-pointer bg-gray-50 border-b border-gray-100">
                                      <div className="flex items-center gap-2 text-[#34656d]">
                                        {isLoading ? <LoadingSpinner /> : (
                                          <div className="w-4 h-4 rounded-full bg-green-500 flex items-center justify-center">
                                            <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                            </svg>
                                          </div>
                                        )}
                                        <span className="font-medium">
                                          {getAgentDisplayName(latestThinkingAgent, currentThinkingMessages[currentThinkingMessages.length - 1]?.content)}
                                        </span>
                                      </div>
                                      <span className="text-sm text-gray-500">
                                        ({currentThinkingMessages.length} steps)
                                      </span>
                                      <ChevronDownIcon />
                                    </summary>
                                    <div className="px-4 py-3 space-y-4">
                                      {currentThinkingMessages.map((thinkingMsg, idx) => (
                                        <div key={thinkingMsg.id} className="border-l-2 border-gray-200 pl-4">
                                          <div className="flex items-center gap-2 mb-2">
                                            <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-1 rounded">
                                              Step {idx + 1}
                                            </span>
                                            <span className="text-xs text-gray-400">
                                              {getAgentDisplayName(thinkingMsg.author, thinkingMsg.content)}
                                            </span>
                                            <div className="ml-auto">
                                              <div className="w-4 h-4 rounded-full bg-green-500 flex items-center justify-center">
                                                <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                </svg>
                                              </div>
                                            </div>
                                          </div>
                                          <div className="text-sm text-gray-600">
                                            {getMessageText(thinkingMsg.content)}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </details>
                                </div>
                              </div>
                            );
                          }

                          return allMessages;
                        })()}
                      </div>
                    </div>
                    {/* Fixed chat composer at bottom of white container */}
                    <div className=" bg-white w-full bottom-0 left-0 z-20 ">
                      <div className="p-3 max-w-2xl mx-auto sticky z-20">
                       <InputBox
                         input={input}
                         setInput={setInput}
                         sendMessage={sendMessage}
                         isLoading={isLoading}
                         isSessionLoading={isSessionLoading}
                         isSessionCreating={isSessionCreating}
                         isTranscribing={isTranscribing}
                         recordingState={recordingState}
                         handleMicPress={handleMicPress}
                         handleMicRelease={handleMicRelease}
                       />
                      </div>
                    </div>
                  </main>
                )}
              </div>
            </div>
            {/* Floating Beta Version badge */}
            <div className="fixed bottom-6 right-6 z-50">
              <span className="bg-[#f1935c] text-white px-4 py-2 rounded-full shadow-lg font-semibold text-sm tracking-wide">
                Beta Version
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );

function TableMarkdown(msg: Message){

  return                                           <ReactMarkdown
  remarkPlugins={[remarkGfm]}
  rehypePlugins={[rehypeRaw, rehypeSanitize]}
  components={{
    // Custom components for markdown elements
    table: ({
      children,
      ...props
    }: React.HTMLProps<HTMLTableElement>) => (
      <div
        className="markdown-table-wrap"
        style={{
          position: "relative",
          marginBottom: "1.5rem",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginBottom: "0.5rem",
          }}
        >
          <button
            className="table-download-btn"
            style={{
              position: "static",
              zIndex: 10,
            }}
            onClick={(e) => {
              // Find the table element inside this container
              const container =
                e.currentTarget.closest(
                  ".markdown-table-wrap"
                );
              const table =
                container?.querySelector(
                  "table"
                );
              if (table)
                downloadTableAsCSV(
                  table as HTMLTableElement
                );
            }}
          >
            <DownloadIcon />
            Download CSV
          </button>
        </div>
        <div className="overflow-x-auto">
          <table
            className="min-w-full"
            {...props}
          >
            {children}
          </table>
        </div>
      </div>
    ),
    thead: ({
      children,
      ...props
    }: React.HTMLProps<HTMLTableSectionElement>) => (
      <thead {...props}>{children}</thead>
    ),
    th: ({
      children,
      ...props
    }: React.HTMLProps<HTMLTableHeaderCellElement>) => (
      <th
        className="px-4 py-3 text-left text-sm font-semibold text-white/90"
        {...props}
      >
        {children}
      </th>
    ),
    td: ({
      children,
      ...props
    }: React.HTMLProps<HTMLTableDataCellElement>) => (
      <td
        className="px-4 py-3 text-sm whitespace-normal text-white/80"
        {...props}
      >
        {children}
      </td>
    ),
    tr: ({
      children,
      ...props
    }: React.HTMLProps<HTMLTableRowElement>) => (
      <tr
        className="transition-colors duration-150"
        {...props}
      >
        {children}
      </tr>
    ),
    code: ({
      inline,
      className,
      children,
      ...props
    }: any) => {
      return inline ? (
        <code
          className="px-1.5 py-0.5 rounded-md bg-gray-700/50 text-gray-100 text-sm"
          {...props}
        >
          {children}
        </code>
      ) : (
        <div className="rounded-md bg-gray-800/50 p-4">
          <code
            className="text-gray-100 text-sm"
            {...props}
          >
            {children}
          </code>
        </div>
      );
    },
    p: ({ children }) => (
      <p className="markdown-paragraph">
        {children}
      </p>
    ),
  }}
>
  {getMessageText(msg.content)}
</ReactMarkdown>
}

}
